import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Info, X } from "lucide-react";

/**
 * Kısa bildirim balonları (sağ alt). alert() yerine; ve başka bir sayfadayken
 * biten işleri haber vermek için (Shell'deki global dinleyici).
 */

export interface ToastInput {
  tone?: "ok" | "danger" | "info";
  title: string;
  body?: string;
  /** Balondaki bağlantı, ör. işin kaydı. */
  link?: { to: string; label: string };
  /** ms; 0 = kullanıcı kapatana kadar. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const ToastContext = createContext<((t: ToastInput) => void) | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (t: ToastInput) => {
      const id = nextId++;
      setItems((prev) => [...prev.slice(-3), { ...t, id }]);
      // Hata balonu kendiliğinden kaybolmaz: okumaya yetişmeyen kaçırmasın.
      const duration = t.duration ?? (t.tone === "danger" ? 0 : 5000);
      if (duration > 0) setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Kapsayıcıda aria-live YOK: her balon kendi rolüyle (status/alert) bir kez okunur. */}
      <div
        className="pointer-events-none fixed right-4 bottom-4 z-50 grid w-[min(360px,calc(100vw-32px))] gap-2"
      >
        {items.map((t) => {
          const color =
            t.tone === "ok"
              ? "var(--color-ok)"
              : t.tone === "danger"
                ? "var(--color-danger)"
                : "var(--color-accent-400)";
          const Icon = t.tone === "ok" ? Check : t.tone === "danger" ? AlertTriangle : Info;
          return (
            <div
              key={t.id}
              role={t.tone === "danger" ? "alert" : "status"}
              className="card animate-toast-in pointer-events-auto flex items-start gap-3 px-3.5 py-3"
              style={{ boxShadow: "var(--shadow-lg)", borderLeft: `2px solid ${color}` }}
            >
              <Icon size={16} style={{ color }} className="mt-[1px] shrink-0" aria-hidden />
              <div className="grid min-w-0 flex-1 gap-0.5">
                <span className="text-sm font-medium text-neutral-100">{t.title}</span>
                {t.body && <span className="text-xs break-words text-neutral-400">{t.body}</span>}
                {t.link && (
                  <Link
                    to={t.link.to}
                    onClick={() => dismiss(t.id)}
                    className="mt-1 text-xs text-accent-300 underline-offset-2 hover:underline"
                  >
                    {t.link.label} →
                  </Link>
                )}
              </div>
              <button
                type="button"
                aria-label="Kapat"
                onClick={() => dismiss(t.id)}
                className="-mt-1 -mr-1 grid size-6 shrink-0 cursor-pointer place-items-center rounded-[var(--radius-sm)] text-neutral-600 hover:bg-white/5 hover:text-neutral-300"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (t: ToastInput) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("ToastProvider eksik");
  return ctx;
}
